import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Alert,
  Spin,
  Button,
  Typography,
  Space,
  Badge,
  Tooltip,
  message
} from 'antd';
import {
  AppstoreOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { dashboardService } from '../services/api';
import DashboardPrint from '../components/print/DashboardPrint';

const { Title, Text } = Typography;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  }).format(amount || 0);

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printLoading, setPrintLoading] = useState(false);

  const printRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardService.getKPIs();
      setData(res.data.data);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'Dashboard - Inventario',
    pageStyle: `@page { size: A4 portrait; margin: 1.5cm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onBeforePrint: () => setPrintLoading(true),
    onAfterPrint: () => setPrintLoading(false),
  });

  // ─── Definición de columnas ──────────────────────────────────────────────────

  const columnsBajoStock = [
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
      render: (clave) => <Text code>{clave}</Text>
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      ellipsis: true
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_nombre',
      key: 'categoria_nombre',
      width: 120,
      render: (val) => val || <Text type="secondary">—</Text>
    },
    {
      title: 'Unidad',
      dataIndex: 'unidad_medida',
      key: 'unidad_medida',
      width: 70,
      align: 'center'
    },
    {
      title: 'Stock Actual',
      dataIndex: 'stock_actual',
      key: 'stock_actual',
      width: 110,
      align: 'right',
      render: (val, record) => (
        <Tag color={parseFloat(val) === 0 ? 'red' : 'orange'} style={{ fontWeight: 600 }}>
          {parseFloat(val).toLocaleString('es-MX')} {record.unidad_medida}
        </Tag>
      )
    },
    {
      title: 'Stock Mín.',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
      width: 100,
      align: 'right',
      render: (val, record) =>
        `${parseFloat(val).toLocaleString('es-MX')} ${record.unidad_medida}`
    },
    {
      title: 'Precio Compra',
      dataIndex: 'precio_compra',
      key: 'precio_compra',
      width: 120,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      render: (_, record) =>
        parseFloat(record.stock_actual) === 0 ? (
          <Tag color="red" icon={<ExclamationCircleOutlined />}>Sin Stock</Tag>
        ) : (
          <Tag color="orange" icon={<ExclamationCircleOutlined />}>Stock Bajo</Tag>
        )
    }
  ];

  const columnsMovimientos = [
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (val) => dayjs(val).format('DD/MM/YY HH:mm')
    },
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 90,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>
    },
    {
      title: 'Producto',
      dataIndex: 'producto_nombre',
      key: 'producto_nombre',
      ellipsis: true,
      render: (nombre, record) => (
        <Tooltip title={`Clave: ${record.clave}`}>
          <span>{nombre}</span>
        </Tooltip>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 90,
      render: (tipo) =>
        tipo === 'entrada' ? (
          <Tag color="success" icon={<ArrowUpOutlined />}>Entrada</Tag>
        ) : (
          <Tag color="error" icon={<ArrowDownOutlined />}>Salida</Tag>
        )
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 95,
      align: 'right',
      render: (val, record) =>
        `${parseFloat(val).toLocaleString('es-MX')} ${record.unidad_medida}`
    },
    {
      title: 'Motivo',
      dataIndex: 'motivo',
      key: 'motivo',
      width: 110,
      render: (val) => val ? <Tag>{val}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      width: 110,
      render: (val) => val || <Text type="secondary">—</Text>
    }
  ];

  const columnsTopProductos = [
    {
      title: '#',
      key: 'rank',
      width: 45,
      render: (_, __, index) => {
        const colors = ['#faad14', '#8c8c8c', '#d46b08', '#1677ff', '#1677ff'];
        return (
          <Badge
            count={index + 1}
            style={{ backgroundColor: colors[index] || '#1677ff' }}
          />
        );
      }
    },
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
      render: (v) => <Text code>{v}</Text>
    },
    {
      title: 'Producto',
      dataIndex: 'nombre',
      key: 'nombre',
      ellipsis: true
    },
    {
      title: 'Movimientos',
      dataIndex: 'total_movimientos',
      key: 'total_movimientos',
      align: 'center',
      width: 100,
      render: (v) => <Tag color="blue">{v}</Tag>
    },
    {
      title: 'Entradas',
      dataIndex: 'total_entradas',
      key: 'total_entradas',
      align: 'right',
      width: 90,
      render: (v) => (
        <Text style={{ color: '#52c41a', fontWeight: 600 }}>
          {parseFloat(v).toLocaleString('es-MX')}
        </Text>
      )
    },
    {
      title: 'Salidas',
      dataIndex: 'total_salidas',
      key: 'total_salidas',
      align: 'right',
      width: 90,
      render: (v) => (
        <Text style={{ color: '#ff4d4f', fontWeight: 600 }}>
          {parseFloat(v).toLocaleString('es-MX')}
        </Text>
      )
    }
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Hidden print component ───────────────────────────────────────────── */}
      <div style={{ display: 'none' }}>
        <DashboardPrint ref={printRef} data={data} />
      </div>

      {/* ── Encabezado de página ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Dashboard</Title>
          <Text type="secondary">
            Resumen general del inventario · {dayjs().format('MMMM YYYY')}
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printLoading}
          >
            Imprimir
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchDashboard}
            loading={loading}
          >
            Actualizar
          </Button>
        </Space>
      </div>

      {/* ── Contenido de pantalla ─────────────────────────────────────────────── */}
      <Spin spinning={loading} tip="Cargando datos...">
        {/* Alertas de stock */}
        {!loading && data && (data.productos_bajo_stock?.length ?? 0) > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={`¡Atención! ${data.productos_bajo_stock.length} producto(s) con stock bajo o agotado`}
            description={
              (data.productos_sin_stock ?? 0) > 0
                ? `${data.productos_sin_stock} producto(s) completamente sin stock.`
                : 'Algunos productos están por debajo del stock mínimo configurado.'
            }
            style={{ marginBottom: 24 }}
            closable
          />
        )}

        {!loading && data && (data.productos_bajo_stock?.length ?? 0) === 0 && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Todos los productos tienen stock suficiente"
            style={{ marginBottom: 24 }}
            closable
          />
        )}

        {/* KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Total Productos Activos"
                value={data?.total_productos ?? 0}
                prefix={<AppstoreOutlined style={{ color: '#1677ff' }} />}
                valueStyle={{ color: '#1677ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Valor del Inventario"
                value={data?.valor_inventario ?? 0}
                prefix={<DollarOutlined style={{ color: '#722ed1' }} />}
                formatter={(val) => formatCurrency(val)}
                valueStyle={{ color: '#722ed1', fontSize: 20 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Entradas del Mes"
                value={data?.total_entradas ?? 0}
                prefix={<ArrowUpOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
                suffix={<Text type="secondary" style={{ fontSize: 14 }}>uds.</Text>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Salidas del Mes"
                value={data?.total_salidas ?? 0}
                prefix={<ArrowDownOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
                suffix={<Text type="secondary" style={{ fontSize: 14 }}>uds.</Text>}
              />
            </Card>
          </Col>
        </Row>

        {/* Tablas */}
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Card
              title={
                <Space>
                  <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  <span>Productos con Stock Bajo</span>
                  {data && (
                    <Badge
                      count={data.productos_bajo_stock?.length ?? 0}
                      style={{
                        backgroundColor: (data.productos_bajo_stock?.length ?? 0) > 0 ? '#faad14' : '#52c41a'
                      }}
                    />
                  )}
                </Space>
              }
              bordered={false}
            >
              <Table
                dataSource={data?.productos_bajo_stock ?? []}
                columns={columnsBajoStock.filter(c =>
                  ['clave', 'nombre', 'stock_actual', 'stock_minimo', 'estado'].includes(c.key)
                )}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5, size: 'small', showSizeChanger: false }}
                locale={{ emptyText: 'Todos los productos tienen stock suficiente ✅' }}
                scroll={{ x: 500 }}
              />
            </Card>
          </Col>

          <Col xs={24} xl={12}>
            <Card
              title={
                <Space>
                  <SwapOutlined />
                  <span>Movimientos Recientes</span>
                </Space>
              }
              bordered={false}
            >
              <Table
                dataSource={data?.movimientos_recientes ?? []}
                columns={columnsMovimientos.filter(c =>
                  ['created_at', 'producto_nombre', 'tipo', 'cantidad'].includes(c.key)
                )}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5, size: 'small', showSizeChanger: false }}
                locale={{ emptyText: 'No hay movimientos registrados aún' }}
                scroll={{ x: 420 }}
              />
            </Card>
          </Col>
        </Row>

        {data?.top_productos_movimiento?.length > 0 && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Top 5 Productos con Más Movimientos" bordered={false}>
                <Table
                  dataSource={data.top_productos_movimiento}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={columnsTopProductos}
                  scroll={{ x: 500 }}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
  );
}

export default Dashboard;
